import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import Highcharts from 'highcharts/highstock';
import styles from './PerformancePopup.module.css';
import { formatCompactCurrency } from '../config/chartConfig';

// How far the popup's anchor point sits from the drawn line's midpoint, in
// pixels, along the chosen perpendicular direction -- chosen so the card's
// own box (see PerformancePopup.module.css: ~200-260px wide, generally
// under ~140px tall with two rows of stats) doesn't visually touch the line
// or the two clicked points once it's centered on that anchor.
const MIN_CLEARANCE_PX = 64;

// Fallback card box size used only for the very first geometry pass, before
// the card has actually been measured via `cardRef` (see the
// `useLayoutEffect` below) -- matches the CSS's `min-width`/typical height.
const FALLBACK_CARD_WIDTH = 220;
const FALLBACK_CARD_HEIGHT = 140;

/**
 * Removes every point from the chart's `'performance-line'` series --
 * the same clearing logic `handlePointClick.js`'s third-click branch uses,
 * replicated here so dismissing the popup (via `×` or backdrop click) also
 * leaves the chart itself clean (no stray line/points).
 */
const clearPerformanceLine = (chart) => {
  const series = chart?.get?.('performance-line');
  if (!series) return;
  while (series.points.length > 0) {
    series.removePoint(0);
  }
};

/**
 * Dismissable popup showing the click-to-measure Change/Change Ann./Before/
 * After stats. Only rendered once both points of the gesture have been
 * clicked (`pointCount === 2`) -- never at 0 or 1. Positioned near the
 * just-drawn `'performance-line'` series: it reads that series' two points'
 * real pixel positions off the chart (via each axis' `toPixels`, which
 * recomputes straight from the axis scale -- more robust across
 * resize/redraw than reading a point's cached `plotX`/`plotY`), then places
 * itself at the line's midpoint offset by `MIN_CLEARANCE_PX` along whichever
 * perpendicular direction points "up" on screen (smaller/negative pixel Y),
 * clamped to stay fully inside the chart container.
 *
 * `containerRef` must point at the chart's nearest `position: relative`
 * ancestor (`Analysis.js` passes its `chartSectionRef`) -- the backdrop and
 * card are positioned `absolute` within it, and its bounding box is what
 * the card gets clamped into.
 */
const PerformancePopup = ({
  pointCount, before, after, change, changeAnn, displayScale, resetPoints, chartRef, containerRef,
}) => {
  const [position, setPosition] = useState(null);
  const cardRef = useRef(null);
  // Once the user drags the card, they own its position -- auto-repositioning
  // (initial placement, resize/redraw re-anchoring) must stop snapping it
  // back. `draggingRef` holds the in-flight pointer-drag state; `movedRef` is
  // the sticky "the user has moved it at least once" flag that gates the
  // computePosition() re-anchor. Both reset when the gesture ends
  // (pointCount !== 2) so a fresh measure starts auto-placed again.
  const draggingRef = useRef(null);
  const movedRef = useRef(false);

  const formatValue = useCallback((value) => (
    displayScale === 'total' ? formatCompactCurrency(value) : Number(value).toFixed(2)
  ), [displayScale]);

  const computePosition = useCallback(() => {
    // The user has taken manual control of the card's position by dragging
    // it -- don't yank it back to the computed anchor on redraw/resize.
    if (movedRef.current) return;
    const chart = chartRef?.current?.chart;
    const sectionEl = containerRef?.current;
    if (!chart || !sectionEl || !chart.container) return;

    const series = chart.get('performance-line');
    const points = series?.points;
    if (!points || points.length < 2) return;

    const xAxis = chart.xAxis?.[0];
    const yAxis = chart.yAxis?.[0];
    if (!xAxis || !yAxis) return;

    const [p1, p2] = points;
    // `toPixels(value, false)` returns the pixel position relative to the
    // chart's own container div (it folds the axis' plotLeft/plotTop offset
    // in for us) -- verified against Highcharts' own source (`Axis.toPixels`
    // adds `this.pos`, the axis' pixel offset, unless `paneCoordinates` is
    // true), and preferred over `plotLeft + point.plotX` because it's
    // recomputed from the axis scale directly rather than relying on a
    // point object's cached translate values.
    const chartPoint1 = { x: xAxis.toPixels(p1.x, false), y: yAxis.toPixels(p1.y, false) };
    const chartPoint2 = { x: xAxis.toPixels(p2.x, false), y: yAxis.toPixels(p2.y, false) };

    // Convert from "relative to the Highcharts container div" to "relative
    // to containerRef" (the position:relative ancestor the popup is
    // absolutely positioned within) by diffing the two elements' rects.
    const chartRect = chart.container.getBoundingClientRect();
    const sectionRect = sectionEl.getBoundingClientRect();
    const offsetX = chartRect.left - sectionRect.left;
    const offsetY = chartRect.top - sectionRect.top;

    const a = { x: offsetX + chartPoint1.x, y: offsetY + chartPoint1.y };
    const b = { x: offsetX + chartPoint2.x, y: offsetY + chartPoint2.y };

    const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.sqrt((dx * dx) + (dy * dy)) || 1;

    // The line's two perpendiculars; pick whichever has the smaller
    // (more negative) pixel-Y component, i.e. "above the line" on screen.
    const perpA = { x: -dy / len, y: dx / len };
    const perpB = { x: dy / len, y: -dx / len };
    const perp = perpA.y <= perpB.y ? perpA : perpB;

    const cardWidth = cardRef.current?.offsetWidth || FALLBACK_CARD_WIDTH;
    const cardHeight = cardRef.current?.offsetHeight || FALLBACK_CARD_HEIGHT;

    const anchorX = mid.x + (perp.x * MIN_CLEARANCE_PX);
    const anchorY = mid.y + (perp.y * MIN_CLEARANCE_PX);

    let left = anchorX - (cardWidth / 2);
    let top = anchorY - (cardHeight / 2);

    // Clamp fully inside the container -- an off-screen popup is worse than
    // one that ends up a little closer to the line than MIN_CLEARANCE_PX.
    const maxLeft = Math.max(sectionRect.width - cardWidth, 0);
    const maxTop = Math.max(sectionRect.height - cardHeight, 0);
    left = Math.min(Math.max(left, 0), maxLeft);
    top = Math.min(Math.max(top, 0), maxTop);

    setPosition({ left, top });
  }, [chartRef, containerRef]);

  // Recompute as soon as the gesture completes (and again once the card
  // itself has rendered, so its real measured size feeds the clamp instead
  // of the fallback estimate) -- a layout effect so this settles before the
  // browser paints, avoiding a visible jump.
  useLayoutEffect(() => {
    if (pointCount !== 2) {
      setPosition(null);
      // A new measure gesture starts auto-placed again -- forget any manual
      // drag from the previous popup.
      movedRef.current = false;
      draggingRef.current = null;
      return;
    }
    computePosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointCount, computePosition]);

  // The chart is responsive (resizes with the viewport) -- keep the popup
  // anchored to the line across a resize or any other Highcharts redraw,
  // but only while it's actually visible.
  useLayoutEffect(() => {
    if (pointCount !== 2) return undefined;

    const handle = () => computePosition();
    window.addEventListener('resize', handle);

    const chart = chartRef?.current?.chart;
    let removeRedrawEvent;
    if (chart && typeof Highcharts.addEvent === 'function') {
      removeRedrawEvent = Highcharts.addEvent(chart, 'redraw', handle);
    }

    return () => {
      window.removeEventListener('resize', handle);
      if (typeof removeRedrawEvent === 'function') removeRedrawEvent();
    };
  }, [pointCount, computePosition, chartRef]);

  const handleDismiss = useCallback(() => {
    clearPerformanceLine(chartRef?.current?.chart);
    resetPoints();
  }, [chartRef, resetPoints]);

  // --- Dragging ---------------------------------------------------------
  // Pointer-based drag on the card body. Records the pointer's offset from
  // the card's current top-left at pointer-down, then on each move sets the
  // new top-left to (pointer - offset), clamped fully inside containerRef so
  // the card can't be dragged off-screen (same clamp computePosition uses).
  // Uses pointer capture so a fast drag that briefly leaves the card still
  // tracks. `stopPropagation` on pointer-down keeps a drag from ever being
  // read as a backdrop click-to-dismiss.
  const handlePointerMove = useCallback((e) => {
    const drag = draggingRef.current;
    const sectionEl = containerRef?.current;
    if (!drag || !sectionEl) return;

    const sectionRect = sectionEl.getBoundingClientRect();
    const card = cardRef.current;
    const cardWidth = card?.offsetWidth || FALLBACK_CARD_WIDTH;
    const cardHeight = card?.offsetHeight || FALLBACK_CARD_HEIGHT;

    let left = e.clientX - sectionRect.left - drag.offsetX;
    let top = e.clientY - sectionRect.top - drag.offsetY;

    const maxLeft = Math.max(sectionRect.width - cardWidth, 0);
    const maxTop = Math.max(sectionRect.height - cardHeight, 0);
    left = Math.min(Math.max(left, 0), maxLeft);
    top = Math.min(Math.max(top, 0), maxTop);

    movedRef.current = true;
    setPosition({ left, top });
  }, [containerRef]);

  const handlePointerUp = useCallback((e) => {
    draggingRef.current = null;
    const card = cardRef.current;
    if (card?.releasePointerCapture && e.pointerId != null) {
      try { card.releasePointerCapture(e.pointerId); } catch (_) { /* already released */ }
    }
  }, []);

  const handlePointerDown = useCallback((e) => {
    // Never start a drag from the close button (let its own click fire).
    if (e.target?.closest?.(`.${styles.closeButton}`)) return;
    e.stopPropagation();

    const card = cardRef.current;
    const sectionEl = containerRef?.current;
    if (!card || !sectionEl) return;

    const cardRect = card.getBoundingClientRect();
    draggingRef.current = {
      offsetX: e.clientX - cardRect.left,
      offsetY: e.clientY - cardRect.top,
    };

    if (card.setPointerCapture && e.pointerId != null) {
      try { card.setPointerCapture(e.pointerId); } catch (_) { /* unsupported */ }
    }
  }, [containerRef]);

  if (pointCount !== 2) return null;

  const cardStyle = position ? { left: position.left, top: position.top } : undefined;

  return (
    <div className={styles.backdrop} data-testid="performance-popup-backdrop" onClick={handleDismiss}>
      <div
        className={styles.card}
        data-testid="performance-popup-card"
        ref={cardRef}
        style={cardStyle}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        <button
          type="button"
          className={styles.closeButton}
          onClick={handleDismiss}
          aria-label="Close performance summary"
        >
          &times;
        </button>
        <div className={styles.grid}>
          <div className={styles.item}>
            <span className={styles.label}>Change</span>
            <span className={styles.value}>{Number(change).toFixed(2)}%</span>
          </div>
          <div className={styles.item}>
            <span className={styles.label}>Change Ann.</span>
            <span className={styles.value}>{Number(changeAnn).toFixed(2)}% p.a.</span>
          </div>
          <div className={styles.item}>
            <span className={styles.label}>Before</span>
            <span className={styles.value}>{formatValue(before)}</span>
          </div>
          <div className={styles.item}>
            <span className={styles.label}>After</span>
            <span className={styles.value}>{formatValue(after)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformancePopup;
